import type { Math } from "@pdfking/quantity"
import { Ast } from "@pdfking/quantity"
import { NullQ } from "@pdfking/structs"
import { Reduce } from "@pdfking/tfunc"

import type { GrowAxis, GrowLayout } from "#public"

/** The axis a container stacks on. */
export const GrowLayoutAxis = (layout: GrowLayout): GrowAxis =>
  layout.enum === "across" ? "across" : "along"

/** The axis a flap sticks out on. */
export const GrowFlapAxis = (flap: GrowLayout & { enum: "flap" }): GrowAxis =>
  flap.edge === "left" || flap.edge === "right" ? "along" : "across"

type Fold = GrowLayout & { enum: "fold" }
type Flap = GrowLayout & { enum: "flap" }

/** A layout of a stack as it takes room: the `fold` creasing it onto the one
 * before, the `flaps` hanging off it. */
export type GrowGroup = { item: GrowLayout; fold: Fold | undefined; flaps: Flap[] }

/** A stack's `layouts` grouped by what takes room: a fold creases the next,
 * a flap hangs off the last; one with nothing to go on is dropped. */
export const GrowStack = (layouts: GrowLayout[]): GrowGroup[] => {
  const groups: GrowGroup[] = []
  let crease: Fold | undefined
  for (const item of layouts) {
    if (item.enum === "fold") crease = item
    else if (item.enum === "flap") {
      if (groups.length) groups[groups.length - 1]!.flaps.push(item)
    } else {
      groups.push({ item, fold: crease, flaps: [] })
      crease = undefined
    }
  }
  return groups
}

// How far a layout reaches on an axis: its own `size`, and what its flaps
// stick out `before` its start and `after` its end — nothing when none do.
type Reach = {
  before: Math | undefined
  size: Math | undefined
  after: Math | undefined
}

// The extents combined by `op`, left to right; nothing of none.
const fold = (extents: (Math | undefined)[], op: (a: Math, b: Math) => Math) =>
  Reduce<Math | undefined, Math | undefined>(
    (total, extent) => (NullQ(extent) ? total : NullQ(total) ? extent : op(total, extent)),
    extents,
    undefined
  )

const max = (extents: (Math | undefined)[]) => fold(extents, Ast.Max)
const add = (extents: (Math | undefined)[]) => fold(extents, Ast.Add)

// `host` with `flap` hung off it, on `axis`
const hung = (host: Reach, flap: Flap, axis: GrowAxis): Reach => {
  const out = GrowFlapAxis(flap)
  const start = flap.edge === "left" || flap.edge === "top"
  if (axis === out) {
    // Sticking out of the edge: as deep as it is, and its own flaps past it
    const deep = reach(flap.layout, out, out)
    if (NullQ(deep) || NullQ(deep.size)) return host
    const total = add([deep.size, deep.after])
    return {
      before: start ? max([host.before, total]) : host.before,
      size: host.size,
      after: start ? host.after : max([host.after, total]),
    }
  }
  // Along the edge: from `from` for its length, its own flaps past either end
  const along = reach(flap.layout, axis, out)
  if (NullQ(along)) return host
  const before = NullQ(along.before) ? undefined : Ast.Sub(along.before, flap.from)
  // To the edge's end when its length is unsaid: nothing of the host past it
  const after =
    NullQ(along.after) || NullQ(host.size) || NullQ(flap.length)
      ? along.after
      : Ast.Sub(along.after, Ast.Sub(host.size, Ast.Add(flap.from, flap.length)))
  return {
    before: max([host.before, before]),
    size: host.size,
    after: max([host.after, after]),
  }
}

// `layout`'s reach on `axis`, `parent` the axis it is stacked on; nothing
// where nothing says.
const reach = (layout: GrowLayout, axis: GrowAxis, parent: GrowAxis): Reach | undefined => {
  if (layout.enum === "fold" || layout.enum === "flap") return undefined
  if (layout.enum === "bleed")
    return axis === parent ? { before: undefined, size: layout.size, after: undefined } : undefined
  if (layout.enum === "content")
    return {
      before: undefined,
      size: axis === parent ? layout.size : layout.cross,
      after: undefined,
    }
  const own = GrowLayoutAxis(layout)
  // Each item's reach, its flaps hung on it — on the one before when it
  // reaches nothing here
  const groups = GrowStack(layout.layouts)
  const items: Reach[] = []
  for (const group of groups) {
    const r = reach(group.item, axis, own)
    if (!NullQ(r)) items.push(r)
    for (const flap of group.flaps) {
      const host = items[items.length - 1]
      if (!NullQ(host)) items[items.length - 1] = hung(host, flap, axis)
    }
  }
  if (axis === own) {
    if (items.length !== groups.length || items.some(r => NullQ(r.size))) return undefined
    // Each item past the ones before it, its flaps past its own ends
    const size = add(items.map(r => r.size))
    let at: Math | undefined
    const befores: (Math | undefined)[] = []
    const afters: (Math | undefined)[] = []
    for (const r of items) {
      befores.push(NullQ(r.before) ? undefined : NullQ(at) ? r.before : Ast.Sub(r.before, at))
      at = add([at, r.size])
      const left = Ast.Sub(size!, at!)
      afters.push(NullQ(r.after) ? undefined : Ast.Sub(r.after, left))
    }
    return { before: max(befores), size, after: max(afters) }
  }
  return {
    before: max(items.map(r => r.before)),
    size: NullQ(layout.cross) ? max(items.map(r => r.size)) : layout.cross,
    after: max(items.map(r => r.after)),
  }
}

/** `layout`'s own extent on `axis`, its flaps aside: the room it takes in
 * its stack. */
export const GrowLayoutCore = (
  layout: GrowLayout,
  axis: GrowAxis,
  parent: GrowAxis
): Math | undefined => {
  const r = reach(layout, axis, parent)
  return NullQ(r) ? undefined : r.size
}

/**
 * How far `layout` reaches on `axis`, as an expression, `parent` being the
 * axis it is stacked on: a leaf's `size` along it, its `cross` the other
 * way; a container the sum of its items on its own axis, its `cross` — or
 * the most any item reaches — on the other; a fold takes no room, a flap
 * what sticks past its host, which spans it. `undefined` where nothing says.
 */
export const GrowLayoutExtent = (
  layout: GrowLayout,
  axis: GrowAxis,
  parent: GrowAxis
): Math | undefined => {
  const r = reach(layout, axis, parent)
  return NullQ(r) || NullQ(r.size) ? undefined : add([r.before, r.size, r.after])
}
